import { useQuery } from '@apollo/client/react';
import { useSplitTreatments, useTrack } from '@splitsoftware/splitio-react';
import { useUser } from 'contexts/userContext';
import { AuthenticatedLayoutDocument } from 'graphql/types';
import { useCallback, useEffect, useState } from 'react';

type SplitFlagsType = Record<string, boolean | object>;

const DEFAULT_FLAGS: SplitFlagsType = {
  'show-developer-menu': false,
  'fit-web-chat': false,
};

const treatmentsToFlags = (treatments: SplitIO.TreatmentsWithConfig) => {
  const flagsSet: SplitFlagsType = {};
  Object.keys(treatments).forEach((treatmentName) => {
    flagsSet[treatmentName] = treatments[treatmentName].treatment === 'on';
  });
  return flagsSet;
};

type TrackArgs = [
  eventType: string,
  value?: number,
  properties?: SplitIO.Properties,
];

type TrackFunction = (...args: TrackArgs) => Promise<void>;

const useSplit = () => {
  const { treatments } = useSplitTreatments({
    names: Object.keys(DEFAULT_FLAGS),
  });
  const flags = treatmentsToFlags(treatments);

  const user = useUser();

  const splitTrack = useTrack();

  // biome-ignore lint/correctness/useExhaustiveDependencies: The Split client does not provide a stable reference
  const track: TrackFunction = useCallback(
    async (eventType, value, props) => {
      const traffic = user?.id ? 'user' : 'anonymous';
      await splitTrack(traffic, eventType, value, props);
    },
    [user?.id],
  );

  return {
    flags,
    track,
  };
};

export const useSplitConfig = () => {
  const { data } = useQuery(AuthenticatedLayoutDocument, {
    fetchPolicy: 'cache-only',
  });
  const self = data?.self;

  // init as anonymous until have the user id: https:// help.split.io/hc/en-us/articles/360046771911-React-SDK-Lazy-initialization-of-Split-client
  const [config, setConfig] = useState<SplitIO.IBrowserSettings | undefined>({
    core: {
      authorizationKey: process.env.REACT_APP_SPLIT_IO_KEY || '',
      key: self?.id ?? 'anonymous',
    },
  });

  const [userAttributes, setUserAttributes] = useState<
    SplitIO.Attributes | undefined
  >();

  useEffect(() => {
    if (self?.id) {
      const { id, email, isTestUser, fitClient } = self;
      setConfig({
        core: {
          authorizationKey: process.env.REACT_APP_SPLIT_IO_KEY || '',
          key: id || '',
        },
      });

      setUserAttributes({
        email: email ?? '',
        isTestUser: isTestUser ?? false,
        clientId: fitClient?.id ?? '',
      });
    }
  }, [self]);

  return { config, userAttributes, user: self };
};

export default useSplit;
